import clsx from "clsx";
import { forwardRef, useState } from "react";
import { twMerge } from "tailwind-merge";

export type ToggleSize = "sm" | "md" | "lg";

export interface ToggleProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "onChange" | "children"
> {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  size?: ToggleSize;
  thumbClassName?: string;
}

const Toggle = forwardRef<HTMLButtonElement, ToggleProps>((props, ref) => {
  const {
    checked: checkedProp,
    defaultChecked = false,
    onChange,
    onClick,
    size = "md",
    className,
    thumbClassName,
    type = "button",
    disabled,
    ...rest
  } = props;

  const isControlled = checkedProp !== undefined;
  const [internalChecked, setInternalChecked] = useState(
    Boolean(defaultChecked),
  );
  const checked = isControlled ? checkedProp : internalChecked;

  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented || disabled) return;

        const nextChecked = !checked;

        if (!isControlled) {
          setInternalChecked(nextChecked);
        }

        onChange?.(nextChecked);
      }}
      className={twMerge(
        clsx(
          "inline-flex items-center rounded-full p-0.5 transition-all duration-200",
          "focus:outline-hidden focus:ring-[3px] focus:ring-primary/25",
          {
            "justify-start w-10 h-5": size === "sm",
            "justify-start w-11 h-6": size === "md",
            "justify-start w-12 h-7": size === "lg",
            "bg-hunter-500 justify-end": checked,
            "bg-gunmetal-300": !checked,
            "opacity-60": disabled,
            "enabled:cursor-pointer": !disabled,
          },
        ),
        className,
      )}
    >
      <span
        className={twMerge(
          clsx("rounded-full bg-white shadow transition-all duration-200", {
            "w-4 h-4": size === "sm",
            "w-5 h-5": size === "md",
            "w-6 h-6": size === "lg",
          }),
          thumbClassName,
        )}
      />
    </button>
  );
});

export default Toggle;
